import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, LoaderCircle, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { readApiJson } from '../lib/api';
import SeoMeta from '../components/SeoMeta';

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General question' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'account', label: 'Account or access' },
  { value: 'feedback', label: 'Feedback' }
];

const ContactDeveloper = () => {
  const { user } = useAuth();
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const signedIn = Boolean(user?.email);
  const guestDetailsValid = signedIn || (
    guestName.trim().length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())
  );
  const canSubmit = guestDetailsValid && subject.trim().length >= 3 && message.trim().length >= 10 && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setNotice(null);

    try {
      const result = await readApiJson('/contact', {
        method: 'POST',
        body: JSON.stringify({
          category,
          subject: subject.trim(),
          message: message.trim(),
          ...(!signedIn ? {
            display_name: guestName.trim(),
            email: guestEmail.trim()
          } : {})
        })
      });
      setSubject('');
      setMessage('');
      setNotice({ type: 'success', text: result?.message || 'Your message has been sent.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Your message could not be sent.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="site-page text-white">
      <SeoMeta
        title="Contact CBAT Academy"
        description="Contact the CBAT Academy developer with a question, technical issue, account request or suggestion. No account is required."
        path="/contact"
        robots="noindex,follow"
      />
      <div className="site-container !max-w-4xl">
        <section className="border-b border-[#25384e] pb-8">
          <div className="site-kicker">Support channel</div>
          <h1 className="site-title">Contact developer</h1>
          <p className="site-subtitle">Report a technical issue, ask a question, or suggest an improvement. You do not need an account to send a message.</p>
        </section>

        <div className="mt-7">
            <form className="site-panel space-y-4 p-5 sm:p-6" onSubmit={handleSubmit}>
              {notice && (
                <div
                  className={`flex items-start gap-2 rounded-md border p-3 text-sm ${notice.type === 'success' ? 'border-[#285b44] bg-[#0b251d] text-[#8ce9bd]' : 'border-[#7c4049] bg-[#2a1118] text-[#ffb3bb]'}`}
                  role={notice.type === 'error' ? 'alert' : 'status'}
                >
                  {notice.type === 'success' ? <CheckCircle2 size={18} aria-hidden="true" /> : <AlertCircle size={18} aria-hidden="true" />}
                  <span>{notice.text}</span>
                </div>
              )}

              {!signedIn && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-[#9db1c5]" htmlFor="contact-name">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      className="h-11 w-full rounded-md border border-[#334c65] bg-[#040a14] px-3 text-sm text-white outline-none placeholder:text-[#52677e] focus:border-[#68d9ff]"
                      type="text"
                      minLength={2}
                      maxLength={120}
                      autoComplete="name"
                      placeholder="Your name"
                      value={guestName}
                      onChange={(event) => setGuestName(event.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-[#9db1c5]" htmlFor="contact-email">
                      Reply email
                    </label>
                    <input
                      id="contact-email"
                      className="h-11 w-full rounded-md border border-[#334c65] bg-[#040a14] px-3 text-sm text-white outline-none placeholder:text-[#52677e] focus:border-[#68d9ff]"
                      type="email"
                      maxLength={254}
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={guestEmail}
                      onChange={(event) => setGuestEmail(event.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              <label className="block text-xs font-semibold text-[#9db1c5]" htmlFor="contact-category">
                Category
              </label>
              <select
                id="contact-category"
                className="h-11 w-full rounded-md border border-[#334c65] bg-[#040a14] px-3 text-sm text-white outline-none focus:border-[#68d9ff]"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <label className="block text-xs font-semibold text-[#9db1c5]" htmlFor="contact-subject">
                Subject
              </label>
              <input
                id="contact-subject"
                className="h-11 w-full rounded-md border border-[#334c65] bg-[#040a14] px-3 text-sm text-white outline-none placeholder:text-[#52677e] focus:border-[#68d9ff]"
                type="text"
                minLength={3}
                maxLength={120}
                placeholder="Briefly describe what you need"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                required
              />

              <div className="flex items-end justify-between gap-3">
                <label className="block text-xs font-semibold text-[#9db1c5]" htmlFor="contact-message">
                  Message
                </label>
                <span className="text-[10px] text-[#7799CC]">{message.length} / 5000</span>
              </div>
              <textarea
                id="contact-message"
                className="min-h-48 w-full resize-y rounded-md border border-[#334c65] bg-[#040a14] p-3 text-sm leading-relaxed text-white outline-none placeholder:text-[#52677e] focus:border-[#68d9ff]"
                minLength={10}
                maxLength={5000}
                placeholder="Include any details that will help diagnose the issue."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
              />

              <button
                className="site-button site-button--primary min-w-40 disabled:cursor-not-allowed"
                type="submit"
                disabled={!canSubmit}
              >
                {submitting ? <LoaderCircle className="animate-spin" size={17} aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
        </div>
      </div>
    </main>
  );
};

export default ContactDeveloper;
